import { contentLocale } from './i18n/messages';
import { getStoredToken } from './utils/authSync';
const API = '/api';

const RETRYABLE_STATUS = new Set([408, 425, 429, 500, 502, 503, 504]);

export class ApiError extends Error {
  constructor(message, status = 0, extra = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = extra.detail || '';
    this.code = extra.code || '';
    this.meta = extra.meta || null;
    this.request = extra.request || extra.meta?.request || null;
    this.retryable = Boolean(extra.retryable);
  }
}

const TECHNICAL_ERROR_RE = /failed to fetch|fetch failed|networkerror|network request failed|load failed|typeerror|aborterror|econnrefused|etimedout|enotfound|socket hang up|unexpected token|internal server error|bad gateway|gateway timeout|service unavailable|net::err_|cannot read propert|is not a function|undefined is not|null is not/i;
const NETWORK_ERROR_RE = /failed to fetch|fetch failed|networkerror|network request failed|load failed|aborterror|econnrefused|etimedout|enotfound|socket hang up|net::err_/i;
const ENGLISH_API_NOISE_RE = /fail|error|exception|invalid|timeout|unavailable|denied|rejected|unable|cannot|could not|not allowed|bad request|unauthorized|forbidden|not found|internal|gateway|unexpected/i;
const TURKISH_SAFE_RE = /[çğıöşüÇĞİÖŞÜ]|yatırım|çekim|tutar|bakiye|cüzdan|hata|lütfen|tekrar|deneyin|minimum|maksimum|geçerli|aktif|talep|oluşturulamadı|alınamadı|kullanılamıyor|yetersiz|eşleşmiyor|bekletme|doğrulanamadı|sunucu|bir hata|ulaşılamıyor|bağlantı/i;
const GENERIC_USER_ERROR = 'İşlem şu an tamamlanamadı.';
const NETWORK_USER_ERROR = 'Bağlantı hatası. Lütfen tekrar deneyin.';

export function toUserFacingError(message, fallback = GENERIC_USER_ERROR) {
  const s = String(message || '').trim();
  if (!s) return fallback;
  if (TECHNICAL_ERROR_RE.test(s) || /\n\s*at\s+/.test(s)) {
    return NETWORK_ERROR_RE.test(s) ? NETWORK_USER_ERROR : fallback;
  }
  if (!TURKISH_SAFE_RE.test(s) && ENGLISH_API_NOISE_RE.test(s)) {
    return NETWORK_ERROR_RE.test(s) ? NETWORK_USER_ERROR : fallback;
  }
  return s;
}

function sanitizeUserFacingDetail(detail, title) {
  const s = String(detail || '').trim();
  if (!s || s === title) return '';
  if (TECHNICAL_ERROR_RE.test(s) || /\n\s*at\s+/.test(s)) return '';
  if (!TURKISH_SAFE_RE.test(s) && ENGLISH_API_NOISE_RE.test(s)) return '';
  return s;
}

export function formatCashierApiError(error, t) {
  const code = error?.code || '';
  const name = error?.meta?.accountHolderMasked || '';
  const fallbackTitle = error?.message || 'Bir hata oluştu';
  const fallbackDetail = error?.detail || '';

  if (typeof t === 'function') {
    if (code === 'PAYURUS_NAME_MISMATCH') {
      return {
        title: t('account.payments.payurus.nameMismatch'),
        detail: name
          ? t('account.payments.payurus.nameMismatchDetail', { name })
          : (fallbackDetail || t('account.payments.payurus.nameMismatchDetail', { name: '—' })),
      };
    }
    if (code === 'PAYURUS_WITHDRAW_RATE_LIMIT') {
      return {
        title: t('account.payments.payurus.rateLimit'),
        detail: t('account.payments.payurus.rateLimitDetail'),
      };
    }
    if (code === 'PAYURUS_WALLET_INVALID') {
      return {
        title: t('account.payments.payurus.walletInvalid'),
        detail: t('account.payments.payurus.walletInvalidDetail'),
      };
    }
    if (code === 'PAYFERRARI_NAME_MISMATCH') {
      return {
        title: t('account.payments.payferrari.nameMismatch'),
        detail: name
          ? t('account.payments.payferrari.nameMismatchDetail', { name })
          : (fallbackDetail || t('account.payments.payferrari.nameMismatchDetail', { name: '—' })),
      };
    }
    if (code === 'PAYFERRARI_WITHDRAW_RATE_LIMIT') {
      return {
        title: t('account.payments.payferrari.rateLimit'),
        detail: t('account.payments.payferrari.rateLimitDetail'),
      };
    }
    if (code === 'PAYFERRARI_WALLET_INVALID') {
      return {
        title: t('account.payments.payferrari.walletInvalid'),
        detail: t('account.payments.payferrari.walletInvalidDetail'),
      };
    }
  }

  const status = Number(error?.status) || 0;
  if (status === 0 || NETWORK_ERROR_RE.test(String(fallbackTitle))) {
    return { title: NETWORK_USER_ERROR, detail: '' };
  }

  const title = toUserFacingError(fallbackTitle, GENERIC_USER_ERROR);
  return { title, detail: sanitizeUserFacingDetail(fallbackDetail, title) };
}

export function isCashierConnectionError(error, formatted) {
  const status = Number(error?.status) || 0;
  const code = String(error?.code || '');
  const title = String(formatted?.title || error?.message || '');
  if (error?.retryable) return true;
  if (status === 0 || status === 502 || status === 503 || status === 504) return true;
  if (/bağlantı hatası/i.test(title)) return true;
  return false;
}

export function isRealActiveCashierLock(error) {
  if (Number(error?.status) !== 429) return false;
  const req = error?.request || error?.meta?.request || null;
  if (!req || typeof req !== 'object') return false;
  const remaining = Number(
    req.remaining_seconds
    ?? error?.meta?.remaining_seconds
    ?? error?.meta?.retryAfter,
  );
  const info = req.account_info;
  const hasAccountInfo = Boolean(
    info && (
      typeof info === 'string'
        ? info.trim()
        : typeof info === 'object'
          ? (info.iban || info.account_number || Object.keys(info).length > 0)
          : false
    ),
  );
  const hasInstrument = Boolean(
    req.iban
    || req.redirect_url
    || req.account_name
    || req.address
    || req.wallet_id
    || hasAccountInfo,
  );
  return Number.isFinite(remaining) && remaining > 0 && hasInstrument;
}

function headers(token, opts = {}) {
  const h = {};
  if (!opts.multipart) h['Content-Type'] = 'application/json';
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
}

function sleep(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function backoffMs(attempt) {
  return Math.min(400 * (2 ** attempt), 4000) + Math.floor(Math.random() * 200);
}

const API_RECONNECT_EVENT = 'vb:api-reconnect';
let reconnectActiveCount = 0;

function networkNoticesReady() {
  try {
    return Boolean(typeof window !== 'undefined' && window.__vbNetworkNoticesReady);
  } catch {
    return false;
  }
}

function emitApiReconnect(active) {
  try {
    if (typeof window === 'undefined') return;
    // Never flash reconnect during initial boot / first paint.
    if (active && !networkNoticesReady()) return;
    window.dispatchEvent(new CustomEvent(API_RECONNECT_EVENT, { detail: { active: Boolean(active) } }));
  } catch {
    /* ignore */
  }
}

function beginApiReconnect() {
  reconnectActiveCount += 1;
  if (reconnectActiveCount === 1) emitApiReconnect(true);
}

function endApiReconnect() {
  if (reconnectActiveCount <= 0) return;
  reconnectActiveCount -= 1;
  if (reconnectActiveCount === 0) emitApiReconnect(false);
}

const DEFAULT_FETCH_TIMEOUT_MS = 12000;
const OTP_SEND_TIMEOUT_MS = 25000;

function isRetryableMethod(method, { retryUnsafe = false } = {}) {
  const m = String(method || 'GET').toUpperCase();
  if (m === 'GET' || m === 'HEAD') return true;
  return Boolean(retryUnsafe);
}

function shouldRetry(method, status, attempt, retries, opts) {
  if (attempt >= retries) return false;
  if (!isRetryableMethod(method, opts)) return false;
  // Unsafe POSTs (cashier deposit creates) must not blindly retry 429:
  // a real active IBAN/redirect should wait, not duplicate-create.
  if (status === 429 && opts.retryUnsafe) return false;
  return RETRYABLE_STATUS.has(status);
}

async function req(path, opts = {}, { retries = 4, soft = false, retryUnsafe = false, timeoutMs = DEFAULT_FETCH_TIMEOUT_MS } = {}) {
  const method = opts.method || 'GET';
  let lastError;
  let reconnectShown = false;

  const markRetrying = () => {
    if (!reconnectShown) {
      beginApiReconnect();
      reconnectShown = true;
    }
  };

  try {
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    let res;
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const externalSignal = opts.signal;
    let onAbort = null;
    let timer = 0;
    if (controller && timeoutMs > 0) {
      timer = window.setTimeout(() => {
        try { controller.abort(); } catch {}
      }, timeoutMs);
    }
    if (controller && externalSignal) {
      if (externalSignal.aborted) {
        try { controller.abort(); } catch {}
      } else {
        onAbort = () => {
          try { controller.abort(); } catch {}
        };
        externalSignal.addEventListener('abort', onAbort, { once: true });
      }
    }
    try {
      const fetchOpts = controller ? { ...opts, signal: controller.signal } : { ...opts };
      res = await fetch(`${API}${path}`, fetchOpts);
    } catch {
      lastError = new ApiError(NETWORK_USER_ERROR, 0);
      if (attempt < retries && isRetryableMethod(method, { retryUnsafe })) {
        markRetrying();
        await sleep(backoffMs(attempt));
        continue;
      }
      if (soft) return null;
      throw lastError;
    } finally {
      if (timer) window.clearTimeout(timer);
      if (externalSignal && onAbort) {
        try { externalSignal.removeEventListener('abort', onAbort); } catch {}
      }
    }

    const data = await res.json().catch(() => ({}));
    if (res.ok) return data;

    if (shouldRetry(method, res.status, attempt, retries, { retryUnsafe })) {
      markRetrying();
      await sleep(backoffMs(attempt));
      continue;
    }

    lastError = new ApiError(
      data.error || (res.status >= 500 ? NETWORK_USER_ERROR : 'Bir hata oluştu'),
      res.status,
      {
        detail: data.errorDetail || data.detail || '',
        code: data.code || '',
        retryable: Boolean(data.retryable),
        request: data.request || null,
        meta: {
          ...(data.meta && typeof data.meta === 'object' ? data.meta : {}),
          ...(data.retryAfter != null ? { retryAfter: data.retryAfter } : {}),
          ...(data.request && typeof data.request === 'object'
            ? { request: data.request, remaining_seconds: data.request.remaining_seconds }
            : {}),
        },
      },
    );
    if (soft) return null;
    throw lastError;
  }

  if (soft) return null;
  throw lastError || new ApiError('Bir hata oluştu', 0);
  } finally {
    if (reconnectShown) endApiReconnect();
  }
}

export const api = {
  login: (body) => req('/auth/login', { method: 'POST', headers: headers(), body: JSON.stringify(body) }, { retries: 5, retryUnsafe: true }),
  sendLoginCode: (body) => req('/auth/login/send-code', { method: 'POST', headers: headers(), body: JSON.stringify(body) }, { retries: 5, retryUnsafe: true, timeoutMs: OTP_SEND_TIMEOUT_MS }),
  verifyLogin: (body) => req('/auth/login/verify', { method: 'POST', headers: headers(), body: JSON.stringify(body) }, { retries: 5, retryUnsafe: true }),
  authSettings: () => req('/auth/settings'),
  exchangeOauthCode: (body) => req('/auth/oauth/exchange', { method: 'POST', headers: headers(), body: JSON.stringify(body) }, { retries: 5, retryUnsafe: true }),
  completeTelegramOauth: (body) => req('/auth/oauth/telegram/complete', { method: 'POST', headers: headers(), body: JSON.stringify(body) }, { retries: 5, retryUnsafe: true }),
  completeProfile: (token, body) => req('/auth/complete-profile', { method: 'POST', headers: headers(token), body: JSON.stringify(body) }, { retries: 5, retryUnsafe: true }),
  register: (body) => req('/auth/register', { method: 'POST', headers: headers(), body: JSON.stringify(body) }, { retries: 5, retryUnsafe: true }),
  sendRegisterCode: (body) => req('/auth/register/send-code', { method: 'POST', headers: headers(), body: JSON.stringify(body) }, { retries: 5, retryUnsafe: true, timeoutMs: OTP_SEND_TIMEOUT_MS }),
  verifyRegister: (body) => req('/auth/register/verify', { method: 'POST', headers: headers(), body: JSON.stringify(body) }, { retries: 5, retryUnsafe: true }),
  sessionSignal: (userId, since) => req(
    `/auth/session-signal?userId=${encodeURIComponent(userId)}&since=${encodeURIComponent(since || 0)}`,
    {},
    { soft: true, retries: 3 },
  ),
  resumeSession: (body) => req('/auth/resume-session', { method: 'POST', headers: headers(), body: JSON.stringify(body) }, { retries: 5, retryUnsafe: true }),
  me: (token) => req('/me', { headers: headers(token) }, { retries: 2, timeoutMs: 10000 }),
  accountSummary: (token) => req('/account/summary', { headers: headers(token) }, { soft: true, retries: 4 }),
  updateProfile: (token, body) => req('/me', { method: 'PATCH', headers: headers(token), body: JSON.stringify(body) }, { retries: 0 }),
  startContactChange: (token, body) => req('/me/contact/start', { method: 'POST', headers: headers(token), body: JSON.stringify(body) }, { retries: 0 }),
  sendContactCode: (token, body) => req('/me/contact/send-code', { method: 'POST', headers: headers(token), body: JSON.stringify(body) }, { retries: 0, timeoutMs: OTP_SEND_TIMEOUT_MS }),
  verifyContactChange: (token, body) => req('/me/contact/verify', { method: 'POST', headers: headers(token), body: JSON.stringify(body) }, { retries: 0 }),
  changePassword: (token, body) => req('/me/password', { method: 'POST', headers: headers(token), body: JSON.stringify(body) }, { retries: 0 }),
  webauthnStatus: () => req('/auth/webauthn/status', {}, { soft: true, retries: 1 }),
  webauthnCredentials: (token) => req('/auth/webauthn/credentials', { headers: headers(token) }),
  webauthnDeleteCredential: (token, id) => req(`/auth/webauthn/credentials/${id}`, { method: 'DELETE', headers: headers(token) }, { retries: 0 }),
  webauthnRegisterOptions: (token) => req('/auth/webauthn/register/options', { method: 'POST', headers: headers(token), body: '{}' }, { retries: 0 }),
  webauthnRegisterVerify: (token, body) => req('/auth/webauthn/register/verify', { method: 'POST', headers: headers(token), body: JSON.stringify(body) }, { retries: 0 }),
  webauthnLoginOptions: () => req('/auth/webauthn/login/options', { method: 'POST', headers: headers(), body: '{}' }, { retries: 5, retryUnsafe: true }),
  webauthnLoginVerify: (body) => req('/auth/webauthn/login/verify', { method: 'POST', headers: headers(), body: JSON.stringify(body) }, { retries: 5, retryUnsafe: true }),
  recoverPassword: (body) => req('/auth/password/recover', { method: 'POST', headers: headers(), body: JSON.stringify(body) }, { retries: 5, retryUnsafe: true }),
  sendPasswordRecoverCode: (body) => req('/auth/password/send-code', { method: 'POST', headers: headers(), body: JSON.stringify(body) }, { retries: 5, retryUnsafe: true, timeoutMs: OTP_SEND_TIMEOUT_MS }),
  verifyPasswordRecover: (body) => req('/auth/password/verify', { method: 'POST', headers: headers(), body: JSON.stringify(body) }, { retries: 5, retryUnsafe: true }),
  resetPassword: (body) => req('/auth/password/reset', { method: 'POST', headers: headers(), body: JSON.stringify(body) }, { retries: 5, retryUnsafe: true }),
  notifications: (token) => req('/notifications', { headers: headers(token) }),
  readNotification: (token, id) => req(`/notifications/${id}/read`, { method: 'PATCH', headers: headers(token) }, { retries: 0 }),
  readAllNotifications: (token) => req('/notifications/read-all', { method: 'PATCH', headers: headers(token) }, { retries: 0 }),
  transactions: (token) => req('/transactions', { headers: headers(token) }),
  deposit: (token, amount, provider = 'direct_crypto') =>
    req('/cashier/deposit', {
      method: 'POST',
      headers: headers(token),
      body: JSON.stringify({ amount, provider }),
    }, { retries: 0 }),
  withdraw: (token, amount, provider = 'direct_crypto') =>
    req('/cashier/withdraw', {
      method: 'POST',
      headers: headers(token),
      body: JSON.stringify({ amount, provider }),
    }, { retries: 0 }),
  paymentMethods: (token) => req('/payment/methods', { headers: headers(token) }),
  cryptoAssets: (token, direction = 'deposit') =>
    req(`/cashier/crypto/assets?direction=${encodeURIComponent(direction)}`, { headers: headers(token) }),
  cryptoDeposit: (token, body) =>
    req('/cashier/crypto/deposit', {
      method: 'POST',
      headers: headers(token),
      body: JSON.stringify(body),
    }, { retries: 0 }),
  cryptoAttachTxHash: (token, id, txHash) =>
    req(`/cashier/crypto/deposit/${id}/tx-hash`, {
      method: 'POST',
      headers: headers(token),
      body: JSON.stringify({ txHash }),
    }, { retries: 0 }),
  cryptoWithdraw: (token, body) =>
    req('/cashier/crypto/withdraw', {
      method: 'POST',
      headers: headers(token),
      body: JSON.stringify(body),
    }, { retries: 0 }),
  payurusDeposit: (token, body) =>
    req('/cashier/payurus/deposit', {
      method: 'POST',
      headers: headers(token),
      body: JSON.stringify(body),
    }, { retries: 2, retryUnsafe: true }),
  payurusCancel: (token, body = {}) =>
    req('/cashier/payurus/cancel', {
      method: 'POST',
      headers: headers(token),
      body: JSON.stringify(body),
    }, { retries: 0 }),
  payurusWithdraw: (token, body) =>
    req('/cashier/payurus/withdraw', {
      method: 'POST',
      headers: headers(token),
      body: JSON.stringify(body),
    }, { retries: 0 }),
  payferrariDeposit: (token, body) =>
    req('/cashier/payferrari/deposit', {
      method: 'POST',
      headers: headers(token),
      body: JSON.stringify(body),
    }, { retries: 2, retryUnsafe: true }),
  payferrariCancel: (token, body = {}) =>
    req('/cashier/payferrari/cancel', {
      method: 'POST',
      headers: headers(token),
      body: JSON.stringify(body),
    }, { retries: 0 }),
  payferrariWithdraw: (token, body) =>
    req('/cashier/payferrari/withdraw', {
      method: 'POST',
      headers: headers(token),
      body: JSON.stringify(body),
    }, { retries: 0 }),
  havaleSpeedDeposit: (token, body) =>
    req('/cashier/havalespeed/deposit', {
      method: 'POST',
      headers: headers(token),
      body: JSON.stringify(body),
    }, { retries: 2, retryUnsafe: true }),
  havaleSpeedConfirm: (token, body = {}) =>
    req('/cashier/havalespeed/confirm', {
      method: 'POST',
      headers: headers(token),
      body: JSON.stringify(body),
    }, { retries: 0 }),
  havaleSpeedCancel: (token, body = {}) =>
    req('/cashier/havalespeed/cancel', {
      method: 'POST',
      headers: headers(token),
      body: JSON.stringify(body),
    }, { retries: 0 }),
  havaleSpeedWithdraw: (token, body) =>
    req('/cashier/havalespeed/withdraw', {
      method: 'POST',
      headers: headers(token),
      body: JSON.stringify(body),
    }, { retries: 0 }),
  maxHavaleDeposit: (token, body) =>
    req('/cashier/maxhavale/deposit', {
      method: 'POST',
      headers: headers(token),
      body: JSON.stringify(body),
    }, { retries: 2, retryUnsafe: true }),
  maxHavaleCancel: (token, body = {}) =>
    req('/cashier/maxhavale/cancel', {
      method: 'POST',
      headers: headers(token),
      body: JSON.stringify(body),
    }, { retries: 0 }),
  maxHavaleWithdraw: (token, body) =>
    req('/cashier/maxhavale/withdraw', {
      method: 'POST',
      headers: headers(token),
      body: JSON.stringify(body),
    }, { retries: 0 }),
  havaleDeposit: (token, body) =>
    req('/cashier/havale/deposit', {
      method: 'POST',
      headers: headers(token),
      body: JSON.stringify(body),
    }, { retries: 2, retryUnsafe: true, timeoutMs: 60000 }),
  havaleCancel: (token, body = {}) =>
    req('/cashier/havale/cancel', {
      method: 'POST',
      headers: headers(token),
      body: JSON.stringify(body),
    }, { retries: 0 }),
  havaleWithdraw: (token, body) =>
    req('/cashier/havale/withdraw', {
      method: 'POST',
      headers: headers(token),
      body: JSON.stringify(body),
    }, { retries: 0 }),
  activeCashierRequest: (token, params = {}) => {
    const q = new URLSearchParams(params).toString();
    return req(`/cashier/active-request${q ? `?${q}` : ''}`, { headers: headers(token) });
  },
  cashierRequest: (token, id) =>
    req(`/cashier/request/${id}`, { headers: headers(token) }),
  submitVerification: (token, body) => {
    if (body instanceof FormData) {
      return req('/account/verification', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body,
      }, { retries: 0 });
    }
    return req('/account/verification', {
      method: 'POST',
      headers: headers(token),
      body: JSON.stringify(body || {}),
    }, { retries: 0 });
  },
  supportTickets: (token) => req('/support/tickets', { headers: headers(token) }),
  supportConfig: (locale = 'tr') => req(`/support/config?locale=${contentLocale(locale)}`),
  createSupportTicket: (token, body) => req('/support/tickets', { method: 'POST', headers: headers(token), body: JSON.stringify(body) }, { retries: 0 }),
  uploadSupportAttachments: (token, files) => {
    const form = new FormData();
    (files || []).forEach((file) => form.append('files', file));
    return req('/support/attachments', {
      method: 'POST',
      headers: headers(token, { multipart: true }),
      body: form,
    }, { retries: 0, timeoutMs: 60000 });
  },
  sports: () => req('/sports'),
  events: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return req(`/events${q ? `?${q}` : ''}`, {}, { soft: true, retries: 5 });
  },
  placeBet: (token, body) => req('/bets', { method: 'POST', headers: headers(token), body: JSON.stringify(body) }, { retries: 0 }),
  myBets: (token) => req('/bets', { headers: headers(token) }),
  historyBets: (token) => req('/account/history/bets', { headers: headers(token) }),
  historyTransactions: (token) => req('/account/history/transactions', { headers: headers(token) }),
  responsibleLimits: (token) => req('/account/responsible-limits', { headers: headers(token) }),
  updateResponsibleLimits: (token, body) => req('/account/responsible-limits', { method: 'PATCH', headers: headers(token), body: JSON.stringify(body) }, { retries: 0 }),
  sessionPing: (token, body = {}) => req('/account/session-ping', {
    method: 'POST',
    headers: headers(token),
    body: JSON.stringify(body),
  }, { soft: true, retries: 3, retryUnsafe: true }),
  vipStatus: (token) => req('/account/vip', { headers: headers(token) }),
  welcomeStatus: (token) => req('/welcome/status', { headers: headers(token) }, { soft: true, retries: 2 }),
  welcomeFsStatus: (token, params = {}) => {
    const q = new URLSearchParams();
    if (params.game_id) q.set('game_id', params.game_id);
    if (params.game_code) q.set('game_code', params.game_code);
    const qs = q.toString();
    return req(`/welcome/fs-status${qs ? `?${qs}` : ''}`, { headers: headers(token) }, { soft: true, retries: 2 });
  },
  welcomeFsActivate: (token) =>
    req('/welcome/fs-activate', { method: 'POST', headers: headers(token), body: '{}' }, { retries: 0 }),
  activateFreespin: (token, body = {}) =>
    req('/promos/freespin/activate', {
      method: 'POST',
      headers: headers(token),
      body: JSON.stringify(body),
    }, { retries: 0 }),
  selectFreespinGame: (token, body = {}) =>
    req('/promos/freespin/select-game', {
      method: 'POST',
      headers: headers(token),
      body: JSON.stringify(body),
    }, { retries: 0 }),
  setWalletMode: (token, mode) =>
    req('/welcome/wallet-mode', {
      method: 'POST',
      headers: headers(token),
      body: JSON.stringify({ mode }),
    }, { retries: 0 }),
  activatePromo: (token, promoId) =>
    req('/promos/activate', {
      method: 'POST',
      headers: headers(token),
      body: JSON.stringify({ promo_id: promoId }),
    }, { retries: 0 }),
  forfeitWalletBonus: (token) =>
    req('/welcome/wallet-bonus/forfeit', {
      method: 'POST',
      headers: headers(token),
      body: JSON.stringify({}),
    }, { retries: 0 }),
  content: (locale = 'tr') => req(`/content?locale=${contentLocale(locale)}`, {}, { soft: true, retries: 5 }),
  casinoProviders: () => req('/casino/providers'),
  casinoGames: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    const token = getStoredToken();
    return req(`/casino/games${q ? `?${q}` : ''}`, { headers: headers(token) });
  },
  casinoVendors: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    const token = getStoredToken();
    return req(`/casino/vendors${q ? `?${q}` : ''}`, { headers: headers(token) });
  },
  casinoStats: () => req('/casino/stats'),
  casinoBadges: () => req('/casino/badges'),
  casinoGame: (id) => {
    const token = getStoredToken();
    return req(`/casino/games/${id}`, { headers: headers(token) });
  },
  launchCasinoGame: (token, body) => req('/casino/launch', { method: 'POST', headers: headers(token), body: JSON.stringify(body) }, { retries: 0 }),
  launchCasinoDemo: (body) => req('/casino/launch-demo', { method: 'POST', headers: headers(), body: JSON.stringify(body) }, { retries: 0 }),
  nexusSportsStatus: () => req('/sports/nexus/status', {}, { soft: true, retries: 3 }),
  nexusTopMatches: (params = {}) => {
    const q = new URLSearchParams();
    if (params.lang) q.set('lang', params.lang);
    if (params.type) q.set('type', params.type);
    if (params.banners || params.slider || params.purpose === 'slider') {
      q.set('banners', '1');
    }
    q.set('fresh', '1');
    q.set('_', String(params._ || Date.now()));
    return req(`/sports/nexus/top-matches?${q.toString()}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json', 'Cache-Control': 'no-cache' },
    }, { soft: true, retries: 2 });
  },
  nexusSportsMenu: (params = {}) => {
    const q = new URLSearchParams();
    if (params.lang) q.set('lang', params.lang);
    if (params.fresh) q.set('fresh', '1');
    q.set('_', String(params._ || Date.now()));
    return req(`/sports/nexus/menu?${q.toString()}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json', 'Cache-Control': 'no-cache' },
    }, { soft: true, retries: 2 });
  },
  getSportsSx: (token) => req('/sports/sx', {
    headers: headers(token),
  }, { soft: true, retries: 1 }),
  launchNexusSportsbook: (token, body = {}) => req('/sports/nexus/launch', {
    method: 'POST',
    headers: headers(token),
    body: JSON.stringify(body),
  }, { retries: 2, retryUnsafe: true, timeoutMs: 25000 }),
  redeemPromoCode: (token, code) =>
    req('/promos/redeem', {
      method: 'POST',
      headers: headers(token),
      body: JSON.stringify({ code }),
    }, { retries: 0 }),
  myPromoRewards: (token, locale = 'tr', tab = 'active') =>
    req(`/promos/my-rewards?locale=${contentLocale(locale)}&tab=${encodeURIComponent(tab || 'active')}`, {
      headers: headers(token),
    }, { soft: true, retries: 2 }),
  tournamentLeaderboard: (promoId, token) =>
    req(`/tournaments/${encodeURIComponent(promoId)}/leaderboard`, {
      headers: headers(token),
    }, { soft: true, retries: 2 }),
  tournamentJoin: (token, promoId) =>
    req(`/tournaments/${encodeURIComponent(promoId)}/join`, {
      method: 'POST',
      headers: headers(token),
      body: JSON.stringify({}),
    }, { retries: 0 }),
  tournamentLeave: (token, promoId) =>
    req(`/tournaments/${encodeURIComponent(promoId)}/leave`, {
      method: 'POST',
      headers: headers(token),
      body: JSON.stringify({}),
    }, { retries: 0 }),
};
